import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CustomerSelect from './CustomerSelect';
import CustomerChat from './CustomerChat';

// WhatsApp Web Style Split Screen Layout for Customer
// Desktop: Left = Menu, Right = Chat
// Mobile: Full screen navigation (existing behavior)

const CustomerDashboardLayout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeChatType, setActiveChatType] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    return !isPWA && window.innerWidth >= 1024;
  });

  // Check for chat type in URL
  useEffect(() => {
    const type = searchParams.get('type');
    if (type) {
      setActiveChatType(type);
    }
  }, [searchParams]);

  // Handle window resize (disabled in PWA mode)
  useEffect(() => {
    const handleResize = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsDesktop(!isPWA && window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle chat selection from CustomerSelect
  const handleChatSelect = (type) => {
    if (isDesktop) {
      // On desktop, open chat in side panel
      setActiveChatType(type);
      // Update URL without navigation
      window.history.pushState({}, '', `/customer-select?type=${type}`);
    } else {
      // On mobile, navigate to full screen chat
      navigate(`/customer-chat?type=${type}`);
    }
  };

  // Handle back button in chat (close side panel)
  const handleCloseChat = () => {
    setActiveChatType(null);
    window.history.pushState({}, '', '/customer-select');
  };

  // Mobile: Use existing full-screen navigation
  if (!isDesktop) {
    return <CustomerSelect onChatSelect={handleChatSelect} />;
  }

  // Desktop: Split screen layout
  return (
    <div className="flex h-screen bg-slate-900 overflow-hidden">
      {/* Left Panel - Menu (fixed width with scroll) */}
      <div 
        className={`${activeChatType ? 'w-[420px] min-w-[420px]' : 'w-full'} h-full transition-all duration-300 ease-in-out overflow-y-auto overflow-x-hidden border-r border-slate-700`}
      >
        <CustomerSelect 
          onChatSelect={handleChatSelect} 
          isCompact={!!activeChatType}
          activeType={activeChatType}
        />
      </div>

      {/* Right Panel - Chat (slides in when active) */}
      {activeChatType && (
        <div className="flex-1 h-full bg-gray-100 relative animate-slideIn overflow-hidden">
          <CustomerChat 
            key={activeChatType}
            chatType={activeChatType} 
            isEmbedded={true}
            onBack={handleCloseChat}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerDashboardLayout;
